<mat-card
  class="item-card"
  [class.selected]="selectedItemId === item.id"
  [class.readonly]="effectiveReadonly"
  [class.processing]="isProcessing"
  [style.margin-left.px]="item.indentLevel * 24"
  [id]="'template-item-' + item.id"
  (click)="!effectiveReadonly && selectItem(item.id); $event.stopPropagation()"
  cdkDrag
  [cdkDragData]="item"
  [cdkDragDisabled]="effectiveReadonly"
>
  @if (!effectiveReadonly) {
    <div class="item-drag-handle" cdkDragHandle>
      <mat-icon>drag_indicator</mat-icon>
    </div>
  }

  <span #headerMarkerRef style="height: 1px"></span>
  <mat-card-header [class.sticky-header]="isHeaderSticky()">
    <div class="item-header-wrapper">
      <div class="item-title-row">
        <div
          class="item-number-badge-wrapper"
          [matTooltip]="
            usePartialMinutesRegeneration && item.isMinutesDirty
              ? ((isNewlyAddedItem ? 'TEMPLATES.EDITOR.NEWLY_ADDED_TOOLTIP' : 'TEMPLATES.EDITOR.NEEDS_REPROCESSING_TOOLTIP') | translate)
              : ''
          "
          [matTooltipDisabled]="!usePartialMinutesRegeneration || !item.isMinutesDirty || isProcessing"
          [matTooltipShowDelay]="0"
        >
          <div class="item-number-badge" [class.selected]="selectedItemId === item.id">
            {{ item.itemNumber }}
          </div>
          @if (usePartialMinutesRegeneration && item.isMinutesDirty && !isProcessing) {
            <span class="stale-dot" [class.new]="isNewlyAddedItem"></span>
          }
        </div>
        @if (isEditingTitle && !effectiveReadonly) {
          <input
            #titleInput
            type="text"
            class="item-title-input"
            [(ngModel)]="temporaryTitle"
            (blur)="onTitleFocusLoss()"
            (keydown)="onTitleKeydown($event)"
            (click)="$event.stopPropagation()"
          />
        } @else {
          <div class="item-title" [class.readonly]="effectiveReadonly" (click)="!effectiveReadonly && onTitleClick(); $event.stopPropagation()">
            {{ item.title }}
            @if (item.isSectionHeading) {
              <span class="section-heading-badge" translate="TEMPLATES.EDITOR.SECTION_HEADING"></span>
            }
          </div>
        }

        @if (!readonly) {
          <div class="header-actions">
            @if (usePartialMinutesRegeneration && item.isMinutesDirty) {
              <span class="reprocess-item-button-wrapper" [matTooltip]="reprocessItemTooltip" [matTooltipShowDelay]="0">
                <button
                  mat-icon-button
                  class="reprocess-item-button"
                  [class.new]="isNewlyAddedItem"
                  [class.processing]="isProcessing"
                  [disabled]="!canReprocessItem"
                  (click)="requestReprocess(); $event.stopPropagation()"
                >
                  @if (isProcessing) {
                    <mat-icon class="spinner-icon">autorenew</mat-icon>
                  } @else {
                    <mat-icon>refresh</mat-icon>
                  }
                </button>
              </span>
            }
            <button mat-icon-button (click)="toggleExpanded(); $event.stopPropagation()">
              <mat-icon>{{ item.expanded ? 'expand_less' : 'expand_more' }}</mat-icon>
            </button>
          </div>
        }
      </div>
      <div class="item-metadata-row" [class.has-indent]="item.indentLevel > 0">
        @if (!item.expanded) {
          <span class="metadata-item" [matTooltip]="blocksTooltip" [matTooltipShowDelay]="0">
            <mat-icon>layers</mat-icon>
            {{ numberOfBlocks }}
          </span>
          @if (numberOfAttachments > 0) {
            <span class="metadata-item" [matTooltip]="attachmentsTooltip" [matTooltipShowDelay]="0">
              <mat-icon>attach_file</mat-icon>
              {{ numberOfAttachments }}
            </span>
          }
        }
        @if (item.duration) {
          <span class="metadata-item" [matTooltip]="'TEMPLATES.EDITOR.DURATION_TOOLTIP' | translate" [matTooltipShowDelay]="0">
            <mat-icon>schedule</mat-icon>
            {{ item.duration }} {{ 'TEMPLATES.EDITOR.MINUTES_SUFFIX' | translate }}
          </span>
        }
        @if (!item.expanded) {
          @if (item.presenters.length > 0) {
            <span class="metadata-item" [matTooltip]="'TEMPLATES.EDITOR.PRESENTERS_TOOLTIP' | translate" [matTooltipShowDelay]="0">
              <mat-icon>people</mat-icon>
              {{ item.presenters.join(', ') }}
            </span>
          }
        }
        @if (!item.expanded && shouldShowTimelineBar) {
          <app-timeline-bar [segments]="item.minuteTimelineSegments" />
        }
      </div>
    </div>
  </mat-card-header>

  @if (item.expanded) {
    <mat-card-content>
      <div class="content-blocks-section">
        <h4><mat-icon>layers</mat-icon>{{ 'TEMPLATES.EDITOR.CONTENT_BLOCKS' | translate }}</h4>
        @if (item.contentBlocks.length === 0) {
          <div class="no-blocks-container">
            <p class="no-blocks">{{ 'TEMPLATES.EDITOR.NO_CONTENT_BLOCKS' | translate }}</p>
            <ng-container [ngTemplateOutlet]="addBlockDivider" [ngTemplateOutletContext]="{ index: 0 }" />
          </div>
        } @else {
          <ng-container class="first-block-divider" [ngTemplateOutlet]="addBlockDivider" [ngTemplateOutletContext]="{ index: 0 }" />

          <div cdkDropList [cdkDropListDisabled]="effectiveReadonly" (cdkDropListDropped)="onBlockDrop($event)" class="blocks-drop-list">
            @for (block of item.contentBlocks; track block.id; let blockIndex = $index) {
              <app-template-item-content-block
                cdkDrag
                [cdkDragDisabled]="effectiveReadonly"
                [block]="block"
                [availableResources]="availableResources"
                [allowFileUpload]="allowFileUpload"
                [shouldFocus]="blockIdToFocus === block.id"
                [systemGuidanceEditingEnabled]="systemGuidanceEditingEnabled"
                [readonly]="effectiveReadonly"
                [isProcessing]="isProcessing"
                (contentChanged)="markAsChanged()"
                (contentChangedInstant)="markAsChangedInstant()"
                (blockRemoved)="removeContentBlock($event)"
                (fileUploadRequested)="onFileUploadRequested($event)"
                (pdfPreviewRequested)="onPdfPreviewRequested($event)"
              />
              <ng-container [ngTemplateOutlet]="addBlockDivider" [ngTemplateOutletContext]="{ index: blockIndex + 1 }" />
            }
          </div>
        }
      </div>

      @if (!effectiveReadonly || item.presenters.length > 0) {
        <div class="presenters-section">
          <h4><mat-icon>groups</mat-icon>{{ 'TEMPLATES.EDITOR.PRESENTERS' | translate }}</h4>

          @if (item.presenters.length > 0) {
            <div class="presenters-list">
              @for (presenter of item.presenters; track $index) {
                <mat-chip [removable]="!effectiveReadonly" (removed)="removePresenter($index)">
                  {{ presenter }}
                  @if (!effectiveReadonly) {
                    <button matChipRemove>
                      <mat-icon>cancel</mat-icon>
                    </button>
                  }
                </mat-chip>
              }
            </div>
          }

          @if (!effectiveReadonly) {
            <div class="add-presenter-input">
              <mat-form-field appearance="outline" class="presenter-input">
                <input
                  matInput
                  placeholder="{{ 'TEMPLATES.EDITOR.ENTER_PRESENTER_NAME' | translate }}"
                  [(ngModel)]="newPresenterName"
                  [matAutocomplete]="presenterAutocomplete"
                  (focus)="filterAttendeeNames()"
                  (input)="filterAttendeeNames()"
                  (keydown.enter)="addPresenter(newPresenterName); $event.stopPropagation()"
                  (click)="$event.stopPropagation()"
                />
                <mat-autocomplete #presenterAutocomplete="matAutocomplete" class="mask-attendee-names" (optionSelected)="addPresenter($event.option.value)">
                  @for (attendeeName of filteredAttendeeNames; track attendeeName) {
                    <mat-option [value]="attendeeName">
                      {{ attendeeName }}
                    </mat-option>
                  }
                </mat-autocomplete>
              </mat-form-field>
              <button mat-stroked-button (click)="addPresenter(newPresenterName); $event.stopPropagation()" [disabled]="newPresenterName.trim() === ''">
                {{ 'TEMPLATES.EDITOR.ADD' | translate }}
              </button>
            </div>
          }
        </div>
      }

      @if (restrictedUsers.length > 0 && (!effectiveReadonly || item.restrictedUserIds.length > 0)) {
        <div class="presenters-section">
          <h4><mat-icon>lock</mat-icon>{{ 'TEMPLATES.EDITOR.RESTRICTED_ACCESS' | translate }}</h4>

          @if (item.restrictedUserIds.length > 0) {
            <div class="presenters-list">
              @for (userId of item.restrictedUserIds; track userId) {
                <mat-chip [removable]="!effectiveReadonly" (removed)="removeRestrictedUser($index)">
                  {{ restrictedUserName(userId) }}
                  @if (!effectiveReadonly) {
                    <button matChipRemove>
                      <mat-icon>cancel</mat-icon>
                    </button>
                  }
                </mat-chip>
              }
            </div>
          }

          @if (!effectiveReadonly && unassignedRestrictedUsers.length > 0) {
            <div class="add-presenter-input">
              <mat-form-field appearance="outline" class="presenter-input">
                <mat-select
                  placeholder="{{ 'TEMPLATES.EDITOR.ADD_RESTRICTED_USER' | translate }}"
                  (selectionChange)="addRestrictedUser($event.value); $event.source.value = null"
                  (click)="$event.stopPropagation()"
                >
                  @for (user of unassignedRestrictedUsers; track user.userId) {
                    <mat-option [value]="user.userId">{{ user.name }}</mat-option>
                  }
                </mat-select>
              </mat-form-field>
            </div>
          }
        </div>
      }
    </mat-card-content>
  }

  <div class="item-drag-placeholder"></div>

  <span #cardMarkerRef class="sticky-header-card-marker"></span>
</mat-card>

<ng-template #addBlockDivider let-index="index">
  @if (effectiveReadonly) {
    <div class="block-spacer"></div>
  } @else {
    <div class="add-block-divider" [class.first-block-divider]="index === 0" (click)="menuTrigger.openMenu(); $event.stopPropagation()">
      <div class="divider-line"></div>
      <button mat-icon-button class="add-block-button" [matMenuTriggerFor]="addBlockMenu" #menuTrigger="matMenuTrigger" (click)="$event.stopPropagation()">
        <mat-icon>add</mat-icon>
      </button>
      <div class="divider-line"></div>
    </div>
  }
  <mat-menu #addBlockMenu="matMenu">
    <button mat-menu-item (click)="addContentBlock(TemplateContentBlockType.FIXED_TEXT, index, null)">
      <span>{{ 'TEMPLATES.EDITOR.BLOCK_TYPES.FIXED_TEXT' | translate }}</span>
    </button>
    <button mat-menu-item (click)="addContentBlock(TemplateContentBlockType.VARIABLE_TEXT, index, null)">
      <span>{{ 'TEMPLATES.EDITOR.BLOCK_TYPES.VARIABLE_TEXT' | translate }}</span>
    </button>
    <div
      [matTooltip]="hasDiscussionSummaryBlock ? ('TEMPLATES.EDITOR.BLOCK_ALREADY_EXISTS' | translate) : ''"
      [matTooltipDisabled]="!hasDiscussionSummaryBlock"
    >
      <button mat-menu-item [disabled]="hasDiscussionSummaryBlock" (click)="addContentBlock(TemplateContentBlockType.DISCUSSION_SUMMARY, index, null)">
        <span>{{ 'TEMPLATES.EDITOR.BLOCK_TYPES.DISCUSSION_SUMMARY' | translate }}</span>
      </button>
    </div>
    <button mat-menu-item [matMenuTriggerFor]="binarySubMenu">
      <span>{{ 'TEMPLATES.EDITOR.BLOCK_TYPES.BINARY' | translate }}</span>
    </button>
  </mat-menu>
  <mat-menu #binarySubMenu="matMenu">
    <div [matTooltip]="hasMattersArisingBlock ? ('TEMPLATES.EDITOR.BLOCK_ALREADY_EXISTS' | translate) : ''" [matTooltipDisabled]="!hasMattersArisingBlock">
      <button mat-menu-item [disabled]="hasMattersArisingBlock" (click)="addContentBlock(TemplateContentBlockType.MATTERS_ARISING, index, null)">
        <span>{{ 'TEMPLATES.EDITOR.BLOCK_TYPES.MATTERS_ARISING' | translate }}</span>
      </button>
    </div>
    <mat-divider />
    <button mat-menu-item (click)="addContentBlock(TemplateContentBlockType.BINARY, index, null)">
      <span>{{ 'TEMPLATES.EDITOR.BLOCK_TYPES.BINARY_CUSTOM' | translate }}</span>
    </button>
    <mat-divider />
    @for (subCategory of binarySubCategories; track subCategory) {
      <button mat-menu-item (click)="addContentBlock(TemplateContentBlockType.BINARY, index, subCategory)">
        <span>{{ 'TEMPLATES.EDITOR.SUB_CATEGORIES.' + subCategory | translate }}</span>
      </button>
    }
  </mat-menu>
</ng-template>
